"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import {
  fetchSkills,
  fetchTags,
  fetchCategories,
  type ClawHubSkill,
  type SkillCategoryItem,
} from "@/lib/clawhub-api";
import type { SortValue } from "@/src/components/skills/sort-dropdown";

const REC_PAGE_SIZE = 9;

export interface SkillsFilterState {
  // Search state
  query: string;
  setQuery: (v: string) => void;
  handleQueryChange: (v: string) => void;
  hasSearched: boolean;
  setHasSearched: (v: boolean) => void;

  // Filter state
  selectedCategory: string;
  setSelectedCategory: (v: string) => void;
  selectedTag: string;
  setSelectedTag: (v: string) => void;
  sortBy: SortValue;
  setSortBy: (v: SortValue) => void;

  // Search results pagination
  skills: ClawHubSkill[];
  loading: boolean;
  loadingMore: boolean;
  total: number;
  hasMore: boolean;
  handleSearch: () => void;
  handleLoadMore: () => void;
  handleTagClick: (tagName: string) => void;
  handleCategoryClick: (catId: string) => void;
  handleSortChange: (val: SortValue) => void;
  handleClear: () => void;
  /** 清除 tag 过滤，保留当前关键词/分类重新搜索 */
  clearTagFilter: () => void;

  // Recommended skills pagination
  recommendedSkills: ClawHubSkill[];
  recHasMore: boolean;
  recLoadingMore: boolean;
  recSentinelRef: React.RefObject<HTMLDivElement>;
  loadMoreRecommended: () => void;

  // Categories (was tags)
  popularTags: SkillCategoryItem[];
  /** 分类列表，来自 /skill/api/v1/categories，含首位“全部”项 */
  categories: SkillCategoryItem[];

  /** 就地更新列表（搜索结果 + 推荐列表）中单个 skill 的互动状态 */
  updateSkill: (updated: ClawHubSkill) => void;

  // Refs
  inputRef: React.RefObject<HTMLInputElement>;
  mainScrollRef: React.RefObject<HTMLDivElement>;
}

export function useSkillsFilter(): SkillsFilterState {
  const [query, setQuery] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTag, setSelectedTag] = useState("");
  const [sortBy, setSortBy] = useState<SortValue>("composite");

  // Data state
  const [skills, setSkills] = useState<ClawHubSkill[]>([]);
  const [popularTags, setPopularTags] = useState<SkillCategoryItem[]>([]);
  const [categories, setCategories] = useState<SkillCategoryItem[]>([]);
  const [recommendedSkills, setRecommendedSkills] = useState<ClawHubSkill[]>(
    [],
  );
  const [recPage, setRecPage] = useState(1);
  const [recHasMore, setRecHasMore] = useState(true);
  const [recLoadingMore, setRecLoadingMore] = useState(false);
  const recSentinelRef = useRef<HTMLDivElement>(null!);
  const mainScrollRef = useRef<HTMLDivElement>(null!);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null!);

  // Load initial data
  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
    fetchTags().then((res) => {
      const tagItems: SkillCategoryItem[] = res.tags.map((tag, i) => ({
        id: i + 1,
        value: tag.name,
        label: tag.label ?? tag.name,
        count: tag.count,
        createdAt: "",
        updatedAt: "",
      }));
      setPopularTags(tagItems);
    });
    // categories 独立来自 /skill/api/v1/categories
    fetchCategories().then((res) => setCategories(res.items));
    fetchSkills({ page: 1, pageSize: REC_PAGE_SIZE, sort: "composite" }).then(
      (res) => {
        setRecommendedSkills(res.skills);
        setRecHasMore(res.hasMore);
        setRecPage(1);
      },
    );
  }, []);

  const loadMoreRecommended = useCallback(() => {
    if (recLoadingMore || !recHasMore) return;
    setRecLoadingMore(true);
    const nextPage = recPage + 1;
    fetchSkills({
      page: nextPage,
      pageSize: REC_PAGE_SIZE,
      sort: "composite",
    }).then((res) => {
      setRecommendedSkills((prev) => {
        const seen = new Set(prev.map((s) => s.id));
        const merged = [...prev];
        for (const s of res.skills) {
          if (!seen.has(s.id)) {
            seen.add(s.id);
            merged.push(s);
          }
        }
        return merged;
      });
      setRecHasMore(res.hasMore);
      setRecPage(nextPage);
      setRecLoadingMore(false);
    });
  }, [recPage, recHasMore, recLoadingMore]);

  // Recommended: intersection observer for infinite scroll
  useEffect(() => {
    if (hasSearched || !recHasMore) return;
    const el = recSentinelRef.current;
    const root = mainScrollRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreRecommended();
      },
      { root: root ?? null, rootMargin: "80px", threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasSearched, recHasMore, loadMoreRecommended]);

  const handleSearch = useCallback(() => {
    if (query.trim() || selectedTag || selectedCategory !== "all") {
      // #tagName 格式：走 tag 过滤，不传 keyword
      const isTagQuery = query.startsWith("#");
      const tagFromQuery = isTagQuery ? query.slice(1).trim() : "";
      const effectiveTag = tagFromQuery || selectedTag;
      const effectiveKeyword = isTagQuery ? "" : query.trim();
      setHasSearched(true);
      setPage(1);
      setLoading(true);
      fetchSkills({
        page: 1,
        pageSize: 12,
        category: selectedCategory,
        tag: effectiveTag,
        keyword: effectiveKeyword,
        sort: sortBy,
      }).then((res) => {
        setSkills(res.skills);
        setTotal(res.total);
        setHasMore(res.hasMore);
        setLoading(false);
      });
    }
  }, [query, selectedTag, selectedCategory, sortBy]);

  const handleTagClick = useCallback(
    (tagName: string) => {
      setSelectedTag(tagName);
      setSelectedCategory("all");
      setQuery(`#${tagName}`);
      setHasSearched(true);
      setPage(1);
      setLoading(true);
      fetchSkills({
        page: 1,
        pageSize: 12,
        tag: tagName,
        sort: sortBy,
      }).then((res) => {
        setSkills(res.skills);
        setTotal(res.total);
        setHasMore(res.hasMore);
        setLoading(false);
      });
    },
    [sortBy],
  );

  const handleCategoryClick = useCallback(
    (catId: string) => {
      setSelectedCategory(catId);
      // 切换分类时清除 tag 过滤和搜索框中的 #tag
      setSelectedTag("");
      setQuery((prev) => (prev.startsWith("#") ? "" : prev));
      const keyword = query.startsWith("#") ? "" : query.trim();
      setHasSearched(true);
      setPage(1);
      setLoading(true);
      fetchSkills({
        page: 1,
        pageSize: 12,
        category: catId === "all" ? "" : catId,
        tag: "",
        keyword,
        sort: sortBy,
      }).then((res) => {
        setSkills(res.skills);
        setTotal(res.total);
        setHasMore(res.hasMore);
        setLoading(false);
      });
    },
    [query, selectedTag, sortBy],
  );

  const handleSortChange = useCallback(
    (val: SortValue) => {
      setSortBy(val);
      setLoading(true);
      const isTagQuery = query.startsWith("#");
      const tagFromQuery = isTagQuery ? query.slice(1).trim() : "";
      const effectiveTag = tagFromQuery || selectedTag;
      fetchSkills({
        page: 1,
        pageSize: 12,
        category: selectedCategory,
        tag: effectiveTag,
        keyword: isTagQuery ? "" : query.trim(),
        sort: val,
      }).then((res) => {
        setSkills(res.skills);
        setTotal(res.total);
        setHasMore(res.hasMore);
        setPage(1);
        setLoading(false);
      });
    },
    [selectedCategory, selectedTag, query],
  );

  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    const isTagQuery = query.startsWith("#");
    const tagFromQuery = isTagQuery ? query.slice(1).trim() : "";
    const effectiveTag = tagFromQuery || selectedTag;
    fetchSkills({
      page: nextPage,
      pageSize: 12,
      category: selectedCategory,
      tag: effectiveTag,
      keyword: isTagQuery ? "" : query.trim(),
      sort: sortBy,
    }).then((res) => {
      setSkills((prev) => [...prev, ...res.skills]);
      setHasMore(res.hasMore);
      setPage(nextPage);
      setLoadingMore(false);
    });
  }, [
    page,
    loadingMore,
    hasMore,
    selectedCategory,
    selectedTag,
    query,
    sortBy,
  ]);

  const handleClear = useCallback(() => {
    setQuery("");
    setSelectedCategory("all");
    setSelectedTag("");
    setHasSearched(false);
    setSkills([]);
    mainScrollRef.current?.scrollTo({ top: 0 });
    inputRef.current?.focus();
  }, []);

  /** 清除 tag 过滤，保留当前关键词/分类重新搜索（顶部 pill bar 点"全部"时使用） */
  const clearTagFilter = useCallback(() => {
    setSelectedTag("");
    const keyword = query.startsWith("#") ? "" : query.trim();
    if (query.startsWith("#")) setQuery("");
    setHasSearched(true);
    setPage(1);
    setLoading(true);
    fetchSkills({
      page: 1,
      pageSize: 12,
      category: selectedCategory,
      tag: "",
      keyword,
      sort: sortBy,
    }).then((res) => {
      setSkills(res.skills);
      setTotal(res.total);
      setHasMore(res.hasMore);
      setLoading(false);
    });
  }, [query, selectedCategory, sortBy]);

  const handleQueryChange = useCallback(
    (v: string) => {
      // 当 query 从 #tag 格式被清空或改为普通文本时，清除 selectedTag
      if (selectedTag && !v.startsWith("#")) {
        setSelectedTag("");
      }
      setQuery(v);
    },
    [selectedTag],
  );

  /** 就地更新列表中单个 skill 的字段（互动状态、计数等） */
  const updateSkill = useCallback((updated: ClawHubSkill) => {
    const patch = (list: ClawHubSkill[]) =>
      list.map((s) => (s.id === updated.id ? updated : s));
    setSkills(patch);
    setRecommendedSkills(patch);
  }, []);

  return {
    query,
    setQuery,
    handleQueryChange,
    hasSearched,
    setHasSearched,
    selectedCategory,
    setSelectedCategory,
    selectedTag,
    setSelectedTag,
    sortBy,
    setSortBy,
    skills,
    loading,
    loadingMore,
    total,
    hasMore,
    handleSearch,
    handleLoadMore,
    handleTagClick,
    handleCategoryClick,
    handleSortChange,
    handleClear,
    clearTagFilter,
    updateSkill,
    recommendedSkills,
    recHasMore,
    recLoadingMore,
    recSentinelRef,
    loadMoreRecommended,
    popularTags,
    categories,
    inputRef,
    mainScrollRef,
  };
}
